import { Injectable } from '@angular/core';
import { AuthToken } from '@app/core/state/app.state';
import { User } from '../store/auth.model';
import { IKeyValueContext } from '@core/models/common.model';
import { STORAGE_KEYS } from '@core/constants/storage.constants';
import { LocalStorageService } from '@core/services/local-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthStorageService {
  private readonly tokenKey = STORAGE_KEYS.AUTH_TOKEN;
  private readonly userKey = STORAGE_KEYS.AUTH_USER;
  private readonly roleKey = STORAGE_KEYS.AUTH_USER_ROLE;
  private readonly authKey = STORAGE_KEYS.AUTHENTICATED;

  constructor(private readonly storage: LocalStorageService) {}

  saveSession(user: User, token: AuthToken): void {
    this.storage.setObject(this.tokenKey, token);
    this.storage.setObject(this.userKey, user);
    this.storage.setObject(this.roleKey, user.role);
    this.storage.setItem(this.authKey, 'true');
  }

  setUser(user: User): void {
    this.storage.setObject(this.userKey, user);
  }

  setCurrentRole(role: IKeyValueContext): void {
    this.storage.setObject(this.roleKey, role);
  }

  clearSession(): void {
    this.storage.removeItem(this.tokenKey);
    this.storage.removeItem(this.userKey);
    this.storage.removeItem(this.roleKey);
    this.storage.removeItem(this.authKey);
  }

  getToken(): AuthToken | null {
    return this.storage.getObject<AuthToken>(this.tokenKey);
  }

  getAccessToken(): string | null {
    return this.getToken()?.token ?? null;
  }

  getUser(): User | null {
    return this.storage.getObject<User>(this.userKey);
  }

  getCurrentRole(): IKeyValueContext | null {
    return this.storage.getObject<IKeyValueContext>(this.roleKey);
  }

  isTokenValid(): boolean {
    const token = this.getToken();
    if (!token?.token || !token.expiresAt) {
      return false;
    }

    return new Date(token.expiresAt).getTime() > Date.now();
  }
}

